import React from "react"
import L10n from "lib/carrot-l10n"
import { FormProvider, useForm } from "react-hook-form"
import { Body, Container, H2 } from "@carrotfertility/carotene"
import { Steps } from "../workflow/steps"
import { useProgressTracker } from "../../views/modal/ProgressTracker"
import { useStateMachine } from "../../context/stateMachine/StateMachineContext"
import useSubmitWithErrorCatch from "../hooks/useSubmitWithErrorCatch"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "../shared"
import { useCurrentUser } from "../../context/user/UserContext"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export const enum HdhpSelectionStatusOptions {
  true = "true",
  false = "false",
  IM_NOT_SURE = "IM_NOT_SURE"
}

const hdhpStatusIdToLabelMapping = {
  [HdhpSelectionStatusOptions.true]: "Yes",
  [HdhpSelectionStatusOptions.false]: "No",
  [HdhpSelectionStatusOptions.IM_NOT_SURE]: "I'm not sure"
}

export const getHdhpStatusLabelFromId = (id: HdhpSelectionStatusOptions): string => hdhpStatusIdToLabelMapping[id]

interface HasHDHPStepProps {
  ViewName: string
  FieldName: string
  updateUserInfo: (val: string) => void
  title: string
  body: string
  hasHDHPValue?: string
}

function HasHdhpStep({
  FieldName,
  ViewName,
  updateUserInfo,
  title,
  body,
  hasHDHPValue
}: HasHDHPStepProps): JSX.Element {
  useProgressTracker(1, "YOUR INSURANCE COVERAGE")
  const formMethods = useForm()
  const { setNextStep } = useStateMachine(ViewName, hasHDHPValue)
  const { onSubmit } = useSubmitWithErrorCatch(async (args) => {
    const val = args[FieldName].id
    val !== "IM_NOT_SURE" && (await updateUserInfo(val))
    setNextStep(ViewName, val)
  })

  const { handleSubmit } = formMethods

  return (
    <FormProvider {...formMethods}>
      <Container padding="none" stack="huge">
        <BenefitEnrollmentFlowModalBackButton />
      </Container>
      <H2 stack="tiny" variant="primary">
        {title}
      </H2>
      <Container padding="none" stack="medium">
        <Body color="black-80">{l`An HDHP is a major medical plan that meets minimum deductible and maximum out-of-pocket 
        expense limitations set by the IRS each year. The current minimum deductible is at least $1,400 for an individual 
        or at least $2,800 for a family, while out-of-pocket expenses can't total more than $7,000 for an individual or $14,000 for a family.`}</Body>
      </Container>
      <Container padding="none" stack="medium">
        <Body color="black-80">{body}</Body>
      </Container>
      <Container padding="none" stack="giant">
        <BenefitEnrollmentModalFormSelect
          name={FieldName}
          defaultValue={hasHDHPValue}
          options={[
            { label: getHdhpStatusLabelFromId(HdhpSelectionStatusOptions.true), id: HdhpSelectionStatusOptions.true },
            { label: getHdhpStatusLabelFromId(HdhpSelectionStatusOptions.false), id: HdhpSelectionStatusOptions.false },
            {
              label: getHdhpStatusLabelFromId(HdhpSelectionStatusOptions.IM_NOT_SURE),
              id: HdhpSelectionStatusOptions.IM_NOT_SURE
            }
          ]}
        />
      </Container>
      <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
    </FormProvider>
  )
}
function MemberHasHdhpIndividualStep(): JSX.Element {
  const { updateUserInfo, memberHasHDHP } = useCurrentUser()
  return (
    <HasHdhpStep
      FieldName={"memberHasHdhpValue"}
      ViewName={Steps.MEMBER_HDHP_INDIVIDUAL}
      updateUserInfo={(val) =>
        updateUserInfo({
          memberHasHDHP: val
        })
      }
      hasHDHPValue={memberHasHDHP}
      title={l`Are you currently enrolled in a high deductible health plan (HDHP)?`}
      body={l`If you’re not sure about your plan details, we’ll connect you with the Care Team to help you figure it out.`}
    />
  )
}

function MemberHasHdhpFamilyStep(): JSX.Element {
  const { updateUserInfo, memberHasHDHP } = useCurrentUser()
  return (
    <HasHdhpStep
      FieldName={"memberHasHdhpValue"}
      ViewName={Steps.MEMBER_HDHP_FAMILY}
      updateUserInfo={(val) =>
        updateUserInfo({
          memberHasHDHP: val
        })
      }
      hasHDHPValue={memberHasHDHP}
      title={l`Are you currently enrolled in a high deductible health plan (HDHP)?`}
      body={l`If you’re not sure about your plan details, we’ll connect you with the Care Team to help you figure it out.`}
    />
  )
}

function PartnerHasHdhpStep(): JSX.Element {
  const { updateUserInfo, partnerHasHDHP } = useCurrentUser()
  return (
    <HasHdhpStep
      FieldName={"partnerHasHdhpValue"}
      ViewName={Steps.PARTNER_HDHP}
      updateUserInfo={(val) =>
        updateUserInfo({
          partnerHasHDHP: val
        })
      }
      hasHDHPValue={partnerHasHDHP}
      title={l`Are they currently enrolled in a high deductible health plan (HDHP)?`}
      body={l`If you’re not sure about their plan details, we’ll connect you with the Care Team to help you figure it out.`}
    />
  )
}
export { MemberHasHdhpIndividualStep, MemberHasHdhpFamilyStep, PartnerHasHdhpStep }
